import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  isDirectorOrHigher,
  isProductOrProjectManager,
} from '@/components/pages/portfolio/portfolio_table.utils';
import type { AuthenticatedUser } from '@/models/auth.model';

dayjs.extend(utc);

/**
 * Utility functions for weekly update calculations and transformations.
 * Centralizes date and status logic to avoid code duplication (DRY principle).
 */

export const EDIT_WINDOW_DAYS = 9; // Monday through Tuesday of next week (inclusive)

export type RoleState = { level?: string; role?: string } | null | undefined;

/**
 * Calculate the Monday of the current week.
 * Business week convention: Monday = start of week, Sunday = end of week.
 * @returns ISO date string (YYYY-MM-DD) of Monday
 */
export const getCurrentWeekMonday = (): string => {
  const now = dayjs.utc();

  const dayOfWeek = now.day();
  const daysBack = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  return now.subtract(daysBack, 'day').format('YYYY-MM-DD');
};

/**
 * Normalize status string to lowercase for comparison.
 * Handles null/undefined safely.
 * @param status - Status string (Green, Yellow, Red, or lowercase variants)
 * @returns Normalized lowercase status or empty string
 */
export const normalizeStatus = (status: string | undefined | null): string => {
  return status?.toLowerCase() ?? '';
};

/**
 * Convert status to health indicator color.
 * @param status - Status string from API/database
 * @returns 'green' | 'yellow' | 'red'
 */
export const statusToHealthColor = (
  status: string | undefined | null,
): 'green' | 'yellow' | 'red' => {
  const normalized = normalizeStatus(status);
  if (normalized === 'green') {
    return 'green';
  }
  if (normalized === 'yellow') {
    return 'yellow';
  }
  return 'red';
};

/**
 * Determine if user can edit health indicators.
 * Checks dev role override first, then falls back to actual user role authorization
 * via imported role-checking functions.
 * @param roleState - Dev role override state (optional)
 * @param user - Authenticated user object with role information
 * @returns true if user can edit health indicators
 */
export const getCanEditHealth = (roleState: RoleState, user: AuthenticatedUser | null): boolean => {
  if (
    roleState?.level === 'director' ||
    roleState?.level === 'vp' ||
    roleState?.role === 'pm' ||
    roleState?.role === 'project_management'
  ) {
    return true;
  }

  return !!user && (isDirectorOrHigher(user) || isProductOrProjectManager(user));
};

/**
 * Check if a week is within the edit window (Monday through Tuesday of next week).
 * Edit window = 9 days from the start of the week.
 * @param week - ISO date string of the week to check (YYYY-MM-DD), or empty string
 * @param allowEmptyWeek - If true, treat empty week as always editable (for create mode)
 * @returns true if within edit window or if allowEmptyWeek is true and week is empty
 */
export const isWeekWithinEditWindow = (week: string, allowEmptyWeek: boolean = false): boolean => {
  if (!week) {
    return allowEmptyWeek;
  }
  const weekStart = dayjs.utc(week);
  const editWindowEnd = weekStart.add(EDIT_WINDOW_DAYS, 'days');
  return dayjs.utc().isBefore(editWindowEnd);
};
